/**
 * Committee desk — the committee member's OWN screen (deliberately separate
 * from the admin console): publish notices, run polls. More rights than a
 * resident, none of the admin console's user/settings powers.
 */
import { Redirect, router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { Icon, type IconName } from '@/features/account-admin/icons';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, Card, Screen, SectionLabel, TopBar } from '@/ui';

type DeskTile = { icon: IconName; label: string; sub: string; path: string };

// Grouped like the admin console (app-wide convention, user ask 2026-07-18).
const TILE_GROUPS: { title: string; tiles: DeskTile[] }[] = [
  {
    title: 'Publishing',
    tiles: [
      { icon: 'megaphone', label: 'Post a notice', sub: 'Urgent notices push the whole society', path: '/admin/create-notice' },
      { icon: 'list', label: 'Notices', sub: 'Everything published so far', path: '/admin/notices' },
      { icon: 'edit', label: 'Start a poll', sub: 'One vote per flat', path: '/admin/create-poll' },
      { icon: 'check', label: 'Polls', sub: 'Live results and closed polls', path: '/admin/polls' },
    ],
  },
  {
    title: 'Governance',
    tiles: [
      { icon: 'calendar', label: 'Meetings', sub: 'Committee meetings, AGM, minutes and quorum', path: '/meetings' },
      { icon: 'alert-circle', label: 'Compliance', sub: 'The statutory clock — audit, returns, AGM, funds', path: '/compliance' },
      { icon: 'check', label: 'Elections', sub: 'Nominations, scrutiny, ballot and results', path: '/elections' },
      { icon: 'credit-card', label: 'Finances', sub: 'Collected vs spent — the expense ledger', path: '/finances' },
      { icon: 'file-text', label: 'Registers', sub: 'Members, minutes book, cash book — always inspection-ready', path: '/registers' },
    ],
  },
  {
    title: 'Approvals & access',
    tiles: [
      { icon: 'clock', label: 'Pending actions', sub: 'Sensitive actions waiting for a second approval', path: '/pending-actions' },
      { icon: 'key', label: 'Shared access', sub: 'Hand a slice of your access to a volunteer', path: '/shared-access' },
    ],
  },
];

export default function CommitteeDesk() {
  const { colors } = useTheme();
  const { user, loading } = useSession();

  if (!loading && user && !['COMMITTEE', 'SUB_ADMIN', 'MASTER_ADMIN'].includes(user.role)) {
    return <Redirect href="/(tabs)/home" />;
  }

  return (
    <Screen scroll={false}>
      <TopBar title="Committee desk" onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <AppText variant="bodyMd" tone="secondary">
          Speak to the whole society — notices and polls go out under the committee&apos;s name.
        </AppText>
        {TILE_GROUPS.map((grp) => (
          <View key={grp.title}>
            <SectionLabel style={{ marginTop: space.s5 }}>{grp.title}</SectionLabel>
            <View style={{ gap: space.s3 }}>
              {grp.tiles.map((t) => (
                <Card key={t.label} onPress={() => router.push(t.path as '/committee')}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                    <View style={{
                      width: 40, height: 40, borderRadius: 12, backgroundColor: colors.primary100,
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon name={t.icon} size={20} color={colors.primary700} />
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText variant="bodyMd" weight="600">{t.label}</AppText>
                      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{t.sub}</AppText>
                    </View>
                    <Icon name="chevron-right" size={18} color={colors.textTertiary} />
                  </View>
                </Card>
              ))}
            </View>
          </View>
        ))}
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s5 }}>
          User management and society settings stay with the society admin.
        </AppText>
      </Screen>
    </Screen>
  );
}
